import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRequestClient } from 'shared/library/api-client';
import { createAdvisorCredentials } from 'shared/library/breed-advisor-credentials';
import {
  advisorAnswerSnapshots,
  advisorMaxFavorites,
  advisorMaxFreeTextLength,
  advisorVersion,
  applyAdvisorFeedback,
  buildAdvisorProfile,
  createAdvisorQuestions,
  createAdvisorSizeAnswer,
  getAdvisorCoverage,
  type AdvisorAnswer,
  type AdvisorCard,
  type AdvisorConflict,
  type AdvisorDraft,
  type AdvisorFeedback,
  type AdvisorFavoriteStage,
  type AdvisorProfile,
  type AdvisorResponse,
  type AdvisorRound,
  type AdvisorSession,
  type AdvisorSharedResult,
  type AdvisorTrait,
  type AdvisorTranslate,
} from 'shared/library/breed-advisor';
import {
  createAdvisorHistory,
  getAdvisorHistoryEntries,
  openAdvisorHistory,
  parseAdvisorHistory,
  parseAdvisorHistoryDraft,
  removeAdvisorHistory,
  startAdvisorHistory,
  updateActiveAdvisorHistory,
  type AdvisorHistory,
} from 'shared/library/breed-advisor-history';
import { useLocale } from 'shared/library/i18n/use-locale';
import { store } from 'shared/library/use-local-storage';
import { useUserStore } from 'shared/state/user';

type State = {
  hydrated: boolean;
  storageKey: string | null;
  answers: AdvisorAnswer[];
  favoriteIds: number[];
  favoriteStage: AdvisorFavoriteStage;
  favoriteInterest?: boolean;
  freeText: string;
  session: AdvisorSession | null;
  rounds: AdvisorRound[];
  credentials: { sessionId: string; token: string } | null;
  submittedAnswerCount: number;
  status: 'questions' | 'loading' | 'results' | 'error';
  error: string | null;
  historyStore: AdvisorHistory | null;
  sessionExpired: boolean;
};
type Refinement = {
  version?: number;
  profile?: AdvisorProfile;
  answers?: { question: string; answer: string }[];
  favorite_breed_ids?: number[];
  free_text?: string;
  feedback?: AdvisorFeedback;
  shown_breed_ids?: number[];
  resolved_conflicts?: { trait: AdvisorTrait; choice: 'answer' | 'text' }[];
  retry_ai?: boolean;
};
type Operation = {
  controller: AbortController;
  storageKey: string;
  generation: number;
};
type DescriptionOperation = Operation & { sessionId: string; revision: string; roundId: string };
type ProgressOperation = Operation & { sessionId: string; promise: Promise<void> };
type DescriptionResponse = {
  revision: string;
  status: NonNullable<AdvisorSession['descriptions_status']>;
  cards: { breed_id: number; summary: string | null }[];
};

function withSummaries(cards: AdvisorCard[], summaries: ReadonlyMap<number, string>) {
  return cards.map((card) =>
    summaries.has(card.breed.id) ? { ...card, summary: summaries.get(card.breed.id)! } : card,
  );
}
const emptyState = (): State => ({
  hydrated: false,
  storageKey: null,
  answers: [],
  favoriteIds: [],
  favoriteStage: 'ask',
  favoriteInterest: undefined,
  freeText: '',
  session: null,
  rounds: [],
  credentials: null,
  submittedAnswerCount: 0,
  status: 'questions',
  error: null,
  historyStore: null,
  sessionExpired: false,
});

function draftFromState(state: State): AdvisorDraft {
  return {
    version: advisorVersion,
    updatedAt: Date.now(),
    answers: state.answers,
    favoriteIds: state.favoriteIds,
    favoriteStage: state.favoriteStage,
    favoriteInterest: state.favoriteInterest,
    freeText: state.freeText,
    rounds: state.rounds,
    submittedAnswerCount: state.submittedAnswerCount,
    ...(state.credentials
      ? { sessionId: state.credentials.sessionId, token: state.credentials.token }
      : {}),
  };
}

function stateFromDraft(
  storageKey: string,
  historyStore: AdvisorHistory,
  draft: AdvisorDraft | null,
): State {
  return {
    ...emptyState(),
    hydrated: true,
    storageKey,
    historyStore,
    ...(draft
      ? {
          answers: draft.answers,
          favoriteIds: draft.favoriteIds,
          favoriteStage: draft.favoriteStage ?? 'ask',
          favoriteInterest: draft.favoriteInterest,
          freeText: draft.freeText,
          rounds: draft.rounds,
          submittedAnswerCount: draft.submittedAnswerCount,
          credentials:
            draft.sessionId && draft.token
              ? { sessionId: draft.sessionId, token: draft.token }
              : null,
          sessionExpired: !!draft.rounds.length && !draft.sessionId,
          status: draft.rounds.length && !draft.sessionId ? 'results' : 'questions',
        }
      : {}),
  };
}

function getProfile(state: State): AdvisorProfile {
  if (!state.session) return buildAdvisorProfile(state.answers);
  return {
    ...state.session.profile,
    ...buildAdvisorProfile(state.answers.slice(state.submittedAnswerCount)),
  };
}

function shownBreedIds(state: State) {
  return Array.from(
    new Set([
      ...(state.session?.shown_breed_ids ?? []),
      ...state.rounds.flatMap((round) =>
        [...round.cards, ...round.favorites].map((card) => card.breed.id),
      ),
    ]),
  ).slice(-100);
}

function conflictKey(conflict: AdvisorConflict) {
  return JSON.stringify([
    conflict.trait,
    conflict.text,
    conflict.original_value,
    conflict.proposed_value,
    conflict.original_mode,
    conflict.proposed_mode,
  ]);
}

export function useBreedAdvisor(t: AdvisorTranslate) {
  const { languageCode, countryCode } = useLocale();
  const apiKey = useUserStore((state) => state.apiKey);
  const userUuid = useUserStore((state) => state.user?.uuid);
  const storageKey = `breed-advisor:v${advisorVersion}:${languageCode}:${countryCode}:${userUuid ?? 'guest'}`;
  const request = useMemo(
    () =>
      createRequestClient({
        languageCode,
        apiKey,
        headers: { 'X-Country': countryCode },
        timeoutMs: 60_000,
      }),
    [languageCode, countryCode, apiKey],
  );
  const progressRequest = useMemo(
    () =>
      createRequestClient({
        languageCode,
        apiKey,
        headers: { 'X-Country': countryCode },
        timeoutMs: 5000,
      }),
    [languageCode, countryCode, apiKey],
  );
  const [state, setRenderedState] = useState<State>(emptyState);
  const current = useRef({ state, t, request, progressRequest, storageKey });
  current.current = { state, t, request, progressRequest, storageKey };
  const setState = useCallback((update: State | ((state: State) => State)) => {
    const next = typeof update === 'function' ? update(current.current.state) : update;
    current.current.state = next;
    setRenderedState(next);
  }, []);
  const operation = useRef<Operation | null>(null);
  const shareOperation = useRef<AbortController | null>(null);
  const descriptionOperation = useRef<DescriptionOperation | null>(null);
  const lastRequest = useRef<{ kind: AdvisorRound['kind']; payload: Refinement } | null>(null);
  const mounted = useRef(false);
  const restoreGeneration = useRef(0);
  const persistence = useRef<Promise<void>>(Promise.resolve());
  const progressOperation = useRef<ProgressOperation | null>(null);
  const progressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedProgress = useRef<string | null>(null);
  const previousProgressText = useRef('');
  const questions = createAdvisorQuestions(t);

  const cancelProgress = useCallback(() => {
    progressOperation.current?.controller.abort();
    progressOperation.current = null;
    if (progressTimer.current) clearTimeout(progressTimer.current);
    progressTimer.current = null;
    savedProgress.current = null;
    previousProgressText.current = '';
  }, []);

  const flushProgress = useCallback((): Promise<void> => {
    if (progressTimer.current) clearTimeout(progressTimer.current);
    progressTimer.current = null;
    if (progressOperation.current) return progressOperation.current.promise;
    const before = current.current.state;
    const credentials = before.credentials;
    if (
      !mounted.current ||
      !before.hydrated ||
      before.storageKey !== current.current.storageKey ||
      before.sessionExpired ||
      !credentials
    )
      return Promise.resolve();

    const active: ProgressOperation = {
      controller: new AbortController(),
      storageKey: current.current.storageKey,
      generation: restoreGeneration.current,
      sessionId: credentials.sessionId,
      promise: Promise.resolve(),
    };
    progressOperation.current = active;
    const isCurrent = () =>
      mounted.current &&
      progressOperation.current === active &&
      restoreGeneration.current === active.generation &&
      current.current.storageKey === active.storageKey &&
      current.current.state.credentials?.sessionId === active.sessionId;

    active.promise = (async () => {
      try {
        // Only one write runs at a time. Answers arriving during a save replace
        // the pending snapshot rather than queueing one request per interaction.
        while (isCurrent()) {
          const state = current.current.state;
          const payload = {
            version: advisorVersion,
            profile: buildAdvisorProfile(state.answers),
            answers: advisorAnswerSnapshots(state.answers),
            favorite_breed_ids: state.favoriteIds,
            ...(state.favoriteInterest !== undefined
              ? { favorite_interest: state.favoriteInterest }
              : {}),
            favorite_stage: state.favoriteStage,
            free_text: state.freeText,
          };
          const signature = JSON.stringify([active.storageKey, active.sessionId, payload]);
          if (signature === savedProgress.current) return;

          // Keep the capability locally before creating its database row, so a
          // lost response can be retried with the same UUID and token.
          await persistence.current;
          if (!isCurrent()) return;
          const result = await current.current.progressRequest<{ uuid: string }>(
            {
              method: 'put',
              url: `/breed-advisor/sessions/${encodeURIComponent(active.sessionId)}/progress`,
            },
            payload,
            { 'X-Advisor-Token': credentials.token },
            active.controller,
          );
          if (!isCurrent()) return;
          if (result.uuid !== active.sessionId) throw new Error('Invalid advisor progress');
          savedProgress.current = signature;
        }
      } finally {
        if (progressOperation.current === active) progressOperation.current = null;
      }
    })();
    return active.promise;
  }, []);

  const saveProgressInBackground = useCallback(() => {
    if (progressOperation.current) return;
    const generation = restoreGeneration.current;
    void flushProgress().catch(() => {
      if (!mounted.current || generation !== restoreGeneration.current) return;
      // Retry once without changing the chat's state. Further changes or a
      // reload retry the latest local snapshot again.
      progressTimer.current = setTimeout(() => {
        progressTimer.current = null;
        void flushProgress().catch(() => {});
      }, 2000);
    });
  }, [flushProgress]);

  const loadDescriptions = useCallback(
    async (retry = false) => {
      const before = current.current.state;
      const session = before.session;
      const credentials = before.credentials;
      const round = before.rounds.at(-1);
      if (
        !mounted.current ||
        !before.hydrated ||
        before.storageKey !== current.current.storageKey ||
        operation.current ||
        descriptionOperation.current ||
        !credentials ||
        !session?.description_revision ||
        !round ||
        before.status !== 'results' ||
        session.conflicts.length ||
        round.descriptionRevision !== session.description_revision
      )
        return;
      const ids = Array.from(
        new Set([...round.cards, ...round.favorites].map((card) => card.breed.id)),
      ).slice(0, 8);
      if (!ids.length) return;
      const active: DescriptionOperation = {
        controller: new AbortController(),
        storageKey: current.current.storageKey,
        generation: restoreGeneration.current,
        sessionId: credentials.sessionId,
        revision: session.description_revision,
        roundId: round.id,
      };
      descriptionOperation.current = active;
      const isCurrent = () =>
        mounted.current &&
        descriptionOperation.current === active &&
        restoreGeneration.current === active.generation &&
        current.current.storageKey === active.storageKey &&
        current.current.state.credentials?.sessionId === active.sessionId &&
        current.current.state.session?.description_revision === active.revision;
      if (retry)
        setState((state) => ({
          ...state,
          session: state.session ? { ...state.session, descriptions_status: 'pending' } : null,
        }));
      try {
        let result: DescriptionResponse | undefined;
        for (let attempt = 0; attempt < 5; attempt++) {
          if (!isCurrent()) return;
          try {
            result = await current.current.request<DescriptionResponse>(
              {
                method: 'post',
                url: `/breed-advisor/sessions/${encodeURIComponent(active.sessionId)}/descriptions`,
              },
              { revision: active.revision, breed_ids: ids, ...(retry ? { retry: true } : {}) },
              { 'X-Advisor-Token': credentials.token },
              active.controller,
            );
            break;
          } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status !== 409 || attempt === 4 || !isCurrent()) throw error;
            // A cancelled batch can still be finishing on the server. Keep the
            // cards usable and retry this temporary lock without another AI retry.
            await new Promise<void>((resolve) => {
              const finish = () => {
                clearTimeout(timer);
                active.controller.signal.removeEventListener('abort', finish);
                resolve();
              };
              const timer = setTimeout(finish, 2000 * 2 ** attempt);
              active.controller.signal.addEventListener('abort', finish, { once: true });
              if (active.controller.signal.aborted) finish();
            });
          }
        }
        if (!result || !isCurrent()) return;
        if (result.revision !== active.revision) throw new Error('Stale advisor descriptions');
        const summaries = new Map(
          result.cards
            .filter((card) => typeof card.summary === 'string' && card.summary.length > 0)
            .map((card) => [card.breed_id, card.summary as string]),
        );
        setState((state) => {
          if (state.session?.description_revision !== active.revision) return state;
          return {
            ...state,
            session: {
              ...state.session,
              descriptions_status: result.status,
              recommendations: withSummaries(state.session.recommendations, summaries),
              favorites: withSummaries(state.session.favorites, summaries),
            },
            rounds: state.rounds.map((round) =>
              round.id === active.roundId && round.descriptionRevision === active.revision
                ? {
                    ...round,
                    cards: withSummaries(round.cards, summaries),
                    favorites: withSummaries(round.favorites, summaries),
                  }
                : round,
            ),
          };
        });
      } catch {
        if (isCurrent())
          setState((state) => ({
            ...state,
            session:
              state.session?.description_revision === active.revision
                ? { ...state.session, descriptions_status: 'unavailable' }
                : state.session,
          }));
      } finally {
        if (descriptionOperation.current === active) descriptionOperation.current = null;
      }
    },
    [setState],
  );

  useEffect(() => {
    if (state.status === 'results' && state.session?.descriptions_status === 'pending') {
      void loadDescriptions();
    }
  }, [
    state.status,
    state.session?.description_revision,
    state.session?.descriptions_status,
    state.rounds.at(-1)?.id,
    loadDescriptions,
  ]);

  const perform = useCallback(
    async (kind: AdvisorRound['kind'], payload: Refinement) => {
      if (!mounted.current || operation.current) return;
      let before = current.current.state;
      if (
        !before.hydrated ||
        before.storageKey !== current.current.storageKey ||
        before.sessionExpired
      )
        return;
      if (!before.credentials) {
        setState({ ...before, credentials: createAdvisorCredentials() });
        before = current.current.state;
      }
      descriptionOperation.current?.controller.abort();
      descriptionOperation.current = null;
      const active: Operation = {
        controller: new AbortController(),
        storageKey: current.current.storageKey,
        generation: restoreGeneration.current,
      };
      operation.current = active;
      lastRequest.current = { kind, payload };
      setState((state) => ({ ...state, status: 'loading', error: null }));
      const isCurrent = () =>
        mounted.current &&
        operation.current === active &&
        restoreGeneration.current === active.generation &&
        current.current.storageKey === active.storageKey;
      try {
        await flushProgress();
        if (!isCurrent()) return;
        const credentials = before.credentials;
        const result = await current.current.request<AdvisorResponse>(
          {
            method: 'post',
            url: credentials
              ? `/breed-advisor/sessions/${encodeURIComponent(credentials.sessionId)}/refine`
              : '/breed-advisor/sessions',
          },
          payload,
          credentials ? { 'X-Advisor-Token': credentials.token } : undefined,
          active.controller,
        );
        if (!isCurrent()) return;
        const token = credentials?.token ?? result.token;
        if (!token || !result.session?.uuid) throw new Error('Missing advisor session');
        setState((state) => {
          let rounds = state.rounds;
          let cards = result.session.recommendations;
          const previousConflicts = new Set(
            rounds.flatMap((round) => (round.conflicts ?? []).map(conflictKey)),
          );
          const conflicts = result.session.conflicts.filter(
            (conflict) => !previousConflicts.has(conflictKey(conflict)),
          );
          const resolution = payload.resolved_conflicts?.[0];
          if (payload.retry_ai && !result.session.description_revision) {
            const updates = new Map(
              [...cards, ...result.session.favorites].map((card) => [card.breed.id, card]),
            );
            const previousIds = new Set(
              rounds.flatMap((round) =>
                [...round.cards, ...round.favorites].map((card) => card.breed.id),
              ),
            );
            rounds = rounds.map((round) => ({
              ...round,
              cards: round.cards.map((card) => updates.get(card.breed.id) ?? card),
              favorites: round.favorites.map((card) => updates.get(card.breed.id) ?? card),
            }));
            cards = cards.filter((card) => !previousIds.has(card.breed.id));
          }
          if (!payload.retry_ai || cards.length || conflicts.length || resolution) {
            rounds = [
              ...rounds,
              {
                id: `${result.session.uuid}-${rounds.length + 1}`,
                ...(result.session.description_revision
                  ? { descriptionRevision: result.session.description_revision }
                  : {}),
                answerCount: state.answers.length,
                kind,
                cards,
                favorites: result.session.favorites,
                ...(payload.feedback ? { feedback: payload.feedback } : {}),
                ...(payload.free_text ? { text: payload.free_text } : {}),
                ...(conflicts.length ? { conflicts } : {}),
                ...(resolution ? { resolution } : {}),
              },
            ];
          }
          return {
            ...state,
            session: result.session,
            credentials: { sessionId: result.session.uuid, token },
            rounds,
            submittedAnswerCount: state.answers.length,
            status: 'results',
            error: null,
          };
        });
      } catch {
        if (isCurrent()) {
          setState((state) => ({
            ...state,
            status: 'error',
            error: current.current.t('request_error'),
          }));
        }
      } finally {
        if (isCurrent()) operation.current = null;
      }
    },
    [flushProgress, setState],
  );

  const loadSession = useCallback(
    async (restored: State) => {
      if (
        !mounted.current ||
        operation.current ||
        restored.storageKey !== current.current.storageKey ||
        !restored.credentials
      )
        return;
      descriptionOperation.current?.controller.abort();
      descriptionOperation.current = null;
      const active: Operation = {
        controller: new AbortController(),
        storageKey: current.current.storageKey,
        generation: restoreGeneration.current,
      };
      operation.current = active;
      const isCurrent = () =>
        mounted.current &&
        operation.current === active &&
        restoreGeneration.current === active.generation &&
        current.current.storageKey === active.storageKey;
      setState({ ...restored, status: 'loading', error: null });
      try {
        const result = await current.current.request<AdvisorResponse>(
          {
            method: 'get',
            url: `/breed-advisor/sessions/${encodeURIComponent(restored.credentials.sessionId)}`,
          },
          undefined,
          { 'X-Advisor-Token': restored.credentials.token },
          active.controller,
        );
        if (!isCurrent()) return;
        const session = result.session;
        const previousConflicts = new Set(
          restored.rounds.flatMap((round) => (round.conflicts ?? []).map(conflictKey)),
        );
        const newConflicts = session.conflicts.filter(
          (conflict) => !previousConflicts.has(conflictKey(conflict)),
        );
        const summaries = new Map(
          [...session.recommendations, ...session.favorites]
            .filter((card) => typeof card.summary === 'string' && card.summary.length > 0)
            .map((card) => [card.breed.id, card.summary as string]),
        );
        const last = restored.rounds.at(-1);
        const sameRound =
          last &&
          (!last.descriptionRevision ||
            last.descriptionRevision === session.description_revision) &&
          last.cards.length === session.recommendations.length &&
          last.cards.every(
            (card, index) => card.breed.id === session.recommendations[index]?.breed.id,
          ) &&
          last.favorites.length === session.favorites.length &&
          last.favorites.every(
            (card, index) => card.breed.id === session.favorites[index]?.breed.id,
          );
        const rounds: AdvisorRound[] = sameRound
          ? restored.rounds.map((round) =>
              round.id === last.id
                ? {
                    ...round,
                    ...(session.description_revision
                      ? { descriptionRevision: session.description_revision }
                      : {}),
                    cards: withSummaries(round.cards, summaries),
                    favorites: withSummaries(round.favorites, summaries),
                    ...(newConflicts.length
                      ? { conflicts: [...(round.conflicts ?? []), ...newConflicts] }
                      : {}),
                  }
                : round,
            )
          : [
              ...restored.rounds,
              {
                id: `${session.uuid}-restored-${restored.rounds.length}`,
                ...(session.description_revision
                  ? { descriptionRevision: session.description_revision }
                  : {}),
                answerCount: restored.submittedAnswerCount,
                kind: 'initial',
                cards: session.recommendations,
                favorites: session.favorites,
                ...(session.conflicts.length ? { conflicts: session.conflicts } : {}),
              },
            ];
        setState({
          ...restored,
          session,
          status: 'results',
          error: null,
          rounds,
        });
      } catch (error) {
        if (!isCurrent()) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 404 || status === 410) {
          setState({
            ...restored,
            credentials: null,
            session: null,
            sessionExpired: true,
            status: restored.rounds.length ? 'results' : 'questions',
            error: null,
          });
        } else {
          // Keep the recoverable session token on transient network failures.
          setState({ ...restored, status: 'error', error: current.current.t('resume_error') });
        }
      } finally {
        if (isCurrent()) operation.current = null;
      }
    },
    [setState],
  );

  useEffect(() => {
    mounted.current = true;
    cancelProgress();
    descriptionOperation.current?.controller.abort();
    descriptionOperation.current = null;
    shareOperation.current?.abort();
    shareOperation.current = null;
    operation.current?.controller.abort();
    operation.current = null;
    lastRequest.current = null;
    const generation = ++restoreGeneration.current;
    let active = true;
    setState(emptyState());
    const restore = async () => {
      let draft: AdvisorDraft | null = null;
      let historyStore: AdvisorHistory | null = null;
      try {
        await persistence.current;
        const [savedDraft, savedHistory] = await Promise.all([
          store.getItem(storageKey),
          store.getItem(`${storageKey}:history`),
        ]);
        draft = parseAdvisorHistoryDraft(savedDraft);
        historyStore = parseAdvisorHistory(savedHistory);
      } catch {
        // Private browsing or device storage failures must not block the conversation.
      }
      if (!active || generation !== restoreGeneration.current) return;
      const restored = stateFromDraft(storageKey, historyStore ?? createAdvisorHistory(), draft);
      if (!restored.credentials || !restored.rounds.length) {
        setState(restored);
        return;
      }
      await loadSession(restored);
    };
    void restore();
    return () => {
      active = false;
      mounted.current = false;
      cancelProgress();
      descriptionOperation.current?.controller.abort();
      descriptionOperation.current = null;
      shareOperation.current?.abort();
      shareOperation.current = null;
      operation.current?.controller.abort();
      operation.current = null;
    };
  }, [storageKey, loadSession, cancelProgress, setState]);

  useEffect(() => {
    if (!state.hydrated || state.storageKey !== storageKey || !state.historyStore) return;
    const draft = draftFromState(state);
    const historyStore = updateActiveAdvisorHistory(state.historyStore, draft);
    // Serialize writes, including reset, so a slower old write cannot restore a reset session.
    persistence.current = persistence.current
      .then(async () => {
        // A full archive must not stop the current draft from being saved.
        await store.setItem(`${storageKey}:history`, JSON.stringify(historyStore)).catch(() => {});
        await store.setItem(storageKey, JSON.stringify(draft));
      })
      .catch(() => {});
  }, [
    state.hydrated,
    state.storageKey,
    state.answers,
    state.favoriteIds,
    state.favoriteStage,
    state.favoriteInterest,
    state.freeText,
    state.rounds,
    state.submittedAnswerCount,
    state.credentials,
    state.historyStore,
    storageKey,
  ]);

  useEffect(() => {
    if (
      !state.hydrated ||
      state.storageKey !== storageKey ||
      state.sessionExpired ||
      (!state.answers.length &&
        state.favoriteInterest === undefined &&
        !state.favoriteIds.length &&
        !state.freeText.trim())
    )
      return;
    if (!state.credentials) {
      setState((state) => ({ ...state, credentials: createAdvisorCredentials() }));
      return;
    }
    const textChanged = previousProgressText.current !== state.freeText;
    previousProgressText.current = state.freeText;
    if (progressTimer.current) clearTimeout(progressTimer.current);
    if (textChanged) {
      progressTimer.current = setTimeout(saveProgressInBackground, 400);
    } else {
      saveProgressInBackground();
    }
    return () => {
      if (progressTimer.current) clearTimeout(progressTimer.current);
      progressTimer.current = null;
    };
  }, [
    state.hydrated,
    state.storageKey,
    state.answers,
    state.favoriteIds,
    state.favoriteStage,
    state.favoriteInterest,
    state.freeText,
    state.credentials,
    state.sessionExpired,
    storageKey,
    saveProgressInBackground,
    setState,
  ]);

  function chooseAnswer(value: string) {
    const state = current.current.state;
    if (
      operation.current ||
      !state.hydrated ||
      state.storageKey !== current.current.storageKey ||
      state.sessionExpired
    )
      return;
    const question = questions.find(
      (question) => !state.answers.some((answer) => answer.questionId === question.id),
    );
    const option = question?.options.find((option) => option.value === value);
    if (!question || !option) return;
    setState((state) => {
      if (state.answers.some((answer) => answer.questionId === question.id)) return state;
      return {
        ...state,
        status: 'questions',
        error: null,
        favoriteStage: 'done',
        answers: [
          ...state.answers,
          { questionId: question.id, value, question: question.text, answer: option.label },
        ],
      };
    });
  }

  function answerSizeRange(min: number, max: number) {
    const state = current.current.state;
    if (
      operation.current ||
      !state.hydrated ||
      state.storageKey !== current.current.storageKey ||
      state.sessionExpired ||
      questions.find(
        (question) => !state.answers.some((answer) => answer.questionId === question.id),
      )?.id !== 'size'
    )
      return;
    const answer = createAdvisorSizeAnswer(min, max, t);
    if (!answer) return;
    setState((state) =>
      state.answers.some((entry) => entry.questionId === 'size')
        ? state
        : {
            ...state,
            status: 'questions',
            error: null,
            favoriteStage: 'done',
            answers: [...state.answers, answer],
          },
    );
  }

  function setFavoriteIds(ids: number[]) {
    const state = current.current.state;
    if (
      operation.current ||
      !state.hydrated ||
      state.storageKey !== current.current.storageKey ||
      state.sessionExpired
    )
      return;
    const favoriteIds = Array.from(new Set(ids.filter((id) => Number.isSafeInteger(id) && id > 0)));
    setState((state) => ({ ...state, favoriteIds: favoriteIds.slice(0, advisorMaxFavorites) }));
  }

  function chooseFavorites(interested: boolean) {
    const state = current.current.state;
    if (
      operation.current ||
      !state.hydrated ||
      state.storageKey !== current.current.storageKey ||
      state.sessionExpired
    )
      return;
    setState((state) => ({
      ...state,
      favoriteStage: interested ? 'select' : 'done',
      favoriteInterest: interested,
      favoriteIds: interested ? state.favoriteIds : [],
    }));
  }

  function finishFavorites() {
    const state = current.current.state;
    if (
      operation.current ||
      !state.hydrated ||
      state.storageKey !== current.current.storageKey ||
      state.sessionExpired
    )
      return;
    setState((state) =>
      state.favoriteStage === 'select' ? { ...state, favoriteStage: 'done' } : state,
    );
  }

  function setFreeText(value: string) {
    const before = current.current.state;
    if (
      !before.hydrated ||
      before.storageKey !== current.current.storageKey ||
      before.sessionExpired
    )
      return;
    setState((state) => ({ ...state, freeText: value.slice(0, advisorMaxFreeTextLength) }));
  }

  async function submit(text = current.current.state.freeText) {
    const state = current.current.state;
    if (!getAdvisorCoverage(state.answers).canMatch || operation.current) return;
    const freeText = text.trim().slice(0, advisorMaxFreeTextLength);
    setFreeText(freeText);
    await perform(state.session ? 'refine' : 'initial', {
      version: advisorVersion,
      profile: getProfile(state),
      answers: advisorAnswerSnapshots(state.answers),
      favorite_breed_ids: state.favoriteIds,
      free_text: freeText,
      ...(state.credentials ? { shown_breed_ids: shownBreedIds(state) } : {}),
    });
  }

  async function refine(feedback: AdvisorFeedback) {
    const state = current.current.state;
    if (!state.session || !state.credentials || operation.current) return;
    const profile = getProfile(state);
    const rejectedCard = [
      ...state.session.recommendations,
      ...state.session.favorites,
      ...state.rounds.flatMap((round) => [...round.cards, ...round.favorites]),
    ].find((card) => card.breed.id === feedback.breed_id);
    const adjusted = applyAdvisorFeedback(profile, feedback, rejectedCard?.traits);
    await perform('refine', {
      feedback,
      shown_breed_ids: shownBreedIds(state),
      ...(JSON.stringify(adjusted) !== JSON.stringify(profile) ? { profile: adjusted } : {}),
    });
  }

  async function more() {
    const state = current.current.state;
    if (!state.session?.has_more || !state.credentials || operation.current) return;
    await perform('more', { shown_breed_ids: shownBreedIds(state) });
  }

  async function resolveConflict(trait: AdvisorTrait, choice: 'answer' | 'text') {
    const state = current.current.state;
    const conflict = state.session?.conflicts.find((conflict) => conflict.trait === trait);
    if (
      !state.credentials ||
      operation.current ||
      !conflict ||
      (choice !== 'answer' && choice !== 'text')
    ) {
      return;
    }
    await perform('conflict', {
      shown_breed_ids: shownBreedIds(state),
      resolved_conflicts: [{ trait, choice }],
    });
  }

  async function retry() {
    if (lastRequest.current) {
      await perform(lastRequest.current.kind, lastRequest.current.payload);
    } else if (current.current.state.credentials) {
      await loadSession(current.current.state);
    } else {
      await submit();
    }
  }

  async function retryAi() {
    const state = current.current.state;
    if (!state.credentials || state.session?.ai_status !== 'unavailable' || operation.current)
      return;
    await perform('refine', { retry_ai: true, shown_breed_ids: shownBreedIds(state) });
  }

  function retryDescriptions() {
    return loadDescriptions(true);
  }

  const createShare = useCallback(async (): Promise<AdvisorSharedResult | null> => {
    const before = current.current.state;
    if (
      before.storageKey !== current.current.storageKey ||
      !before.hydrated ||
      !before.credentials ||
      !before.session ||
      before.status !== 'results'
    )
      return null;
    const { sessionId, token } = before.credentials;
    const key = current.current.storageKey;
    const generation = restoreGeneration.current;
    const disliked = new Set(
      before.session.feedback
        .filter((item) => item.reaction === 'dislike')
        .map((item) => item.breed_id),
    );
    const ids = Array.from(
      new Set(
        [...before.session.recommendations, ...before.session.favorites]
          .map((card) => card.breed.id)
          .filter((id) => !disliked.has(id)),
      ),
    ).slice(0, 10);
    shareOperation.current?.abort();
    const controller = new AbortController();
    shareOperation.current = controller;
    const result = await current.current.request<{ share: AdvisorSharedResult }>(
      { method: 'post', url: `/breed-advisor/sessions/${encodeURIComponent(sessionId)}/shares` },
      ids.length ? { breed_ids: ids } : {},
      { 'X-Advisor-Token': token },
      controller,
    );
    if (
      !mounted.current ||
      shareOperation.current !== controller ||
      generation !== restoreGeneration.current ||
      controller.signal.aborted ||
      key !== current.current.storageKey ||
      current.current.state.credentials?.sessionId !== sessionId
    )
      return null;
    return result.share;
  }, []);

  function reset() {
    const before = current.current.state;
    if (
      !before.hydrated ||
      before.storageKey !== current.current.storageKey ||
      !before.historyStore
    )
      return;
    cancelOperations();
    setState(
      stateFromDraft(
        before.storageKey,
        startAdvisorHistory(before.historyStore, draftFromState(before)),
        null,
      ),
    );
  }

  function cancelOperations() {
    restoreGeneration.current += 1;
    cancelProgress();
    operation.current?.controller.abort();
    descriptionOperation.current?.controller.abort();
    descriptionOperation.current = null;
    shareOperation.current?.abort();
    shareOperation.current = null;
    operation.current = null;
    lastRequest.current = null;
  }

  async function openHistory(id: string) {
    const before = current.current.state;
    if (
      !before.hydrated ||
      before.storageKey !== current.current.storageKey ||
      !before.historyStore
    )
      return;
    const selection = openAdvisorHistory(before.historyStore, draftFromState(before), id);
    if (!selection) return;
    cancelOperations();
    const restored = stateFromDraft(before.storageKey, selection.history, selection.draft);
    setState(restored);
    if (restored.credentials && restored.rounds.length) await loadSession(restored);
  }

  function removeHistory(id: string) {
    const before = current.current.state;
    if (
      !before.hydrated ||
      before.storageKey !== current.current.storageKey ||
      !before.historyStore
    )
      return;
    setState({ ...before, historyStore: removeAdvisorHistory(before.historyStore, id) });
  }

  const history = useMemo(
    () =>
      state.historyStore ? getAdvisorHistoryEntries(state.historyStore, draftFromState(state)) : [],
    [state],
  );
  const { historyStore, ...visibleState } = state;

  return {
    ...visibleState,
    history,
    activeHistoryId: historyStore?.activeId ?? null,
    openHistory,
    removeHistory,
    questions,
    question: questions.find(
      (question) => !state.answers.some((answer) => answer.questionId === question.id),
    ),
    profile: getProfile(state),
    coverage: getAdvisorCoverage(state.answers),
    setFavoriteIds,
    chooseFavorites,
    finishFavorites,
    setFreeText,
    chooseAnswer,
    answerSizeRange,
    submit,
    refine,
    more,
    resolveConflict,
    retry,
    retryAi,
    retryDescriptions,
    createShare,
    reset,
  };
}
